import { useEffect, useSyncExternalStore } from "react";
import { useT } from "../i18n";
import { ATTRAPPE, hole, sende } from "../daten/klient";

/* ══════════════════════════════════════════════════════════════════
   Push — **nicht zum Einschalten, sondern von selbst** (Luca, 24.09.2026:
   *„Die Meldungen soll nicht der User selber einstellen können, sondern
   Push soll automatisch abgefragt werden."*).

   Wer angemeldet ist, wird beim ersten Tipp in die Anwendung einmal vom
   Browser gefragt, ob Benachrichtigungen kommen dürfen. Die Frage hängt am
   Tipp und nicht am Laden, weil Safari und das iPhone sie nur nach einer
   Handlung zulassen und Chrome eine ungefragte Abfrage stumm schaltet. Ist
   sie schon beantwortet, wird das Abo still nachgezogen — auch nach einer
   Neuinstallation. Ob dann wirklich etwas hinausgeht, stellt der Admin je
   Person ein (Einstellungen → Benachrichtigungen).

   Was nicht geht, sagt die Glocke unter „Für mich" (CLAUDE.md, „Was heute
   nicht wirkt"):
   - Die Fassung hat keinen Schlüssel → „noch nicht eingerichtet".
   - Der Browser kann kein Push → „dieser Browser kann es nicht".
   - iPhone ohne installierte App → „nur in der installierten App".
   - Die Erlaubnis wurde verweigert → „im Browser erlauben".
   ══════════════════════════════════════════════════════════════════ */

export type Pushstand = "laedt" | "aus_fassung" | "kein_browser" | "iphone_installieren" | "verweigert" | "fragt" | "an" | "fehler";

/** Was beim Anmelden schiefging — steht unter „Für mich", damit man es sieht. */
let letzterFehler = "";
export const pushFehler = () => letzterFehler;

/** „Chrome · Android" statt „Telefon": Welches Gerät das ist, sagt der Browser. */
export function geraetename(): string {
  const ua = navigator.userAgent;
  const browser = /Edg\//.test(ua) ? "Edge" : /OPR\//.test(ua) ? "Opera" : /SamsungBrowser/.test(ua) ? "Samsung Internet"
    : /Firefox\//.test(ua) ? "Firefox" : /Chrome\//.test(ua) ? "Chrome" : /Safari\//.test(ua) ? "Safari" : "Browser";
  const system = /Android/.test(ua) ? "Android" : /iPhone|iPad/.test(ua) ? "iOS" : /Mac OS X/.test(ua) ? "macOS"
    : /Windows/.test(ua) ? "Windows" : /Linux/.test(ua) ? "Linux" : "";
  return system ? `${browser} · ${system}` : browser;
}

/** Das Abo neu ziehen — nach dem Löschen eines Geräts oder von Hand. */
let neuAnmelden: (() => void) | null = null;
export const pushNeuAnmelden = () => neuAnmelden?.();

let stand: Pushstand = "laedt";
const hoerer = new Set<() => void>();
function setze(neu: Pushstand) {
  stand = neu;
  hoerer.forEach((h) => h());
}
const anmelden = (h: () => void) => { hoerer.add(h); return () => { hoerer.delete(h); }; };

/** Wie es um Push auf diesem Gerät steht — für die Zeile in der Glocke. */
export const usePushstand = () => useSyncExternalStore(anmelden, () => stand, () => stand);

function schluesselAlsBytes(b64: string): Uint8Array {
  const auffuellen = "=".repeat((4 - (b64.length % 4)) % 4);
  const roh = atob((b64 + auffuellen).replace(/-/g, "+").replace(/_/g, "/"));
  return Uint8Array.from([...roh].map((c) => c.charCodeAt(0)));
}

async function abonnieren(schluessel: string, geraet: string): Promise<void> {
  letzterFehler = "";
  const reg = await navigator.serviceWorker.ready;
  const abo = await reg.pushManager.getSubscription()
    ?? await reg.pushManager.subscribe({
      userVisibleOnly: true, applicationServerKey: schluesselAlsBytes(schluessel) as BufferSource,
    });
  const json = abo.toJSON() as { endpoint?: string; keys?: { p256dh?: string; auth?: string } };
  /* Jedes Mal melden: Die API legt es nur einmal an (je Endpunkt) und weiß
     danach, dass das Gerät noch lebt. */
  await sende("/meldungen/geraete", { endpoint: json.endpoint, keys: json.keys, label: geraet });
  setze("an");
}

function scheitern(f: unknown) {
  letzterFehler = f instanceof Error ? `${f.name}: ${f.message}` : String(f);
  setze("fehler");
}

/** Hängt einmal in der Schale (über die Glocke) und zeichnet nichts. */
export function PushAutomatik() {
  useEffect(() => {
    if (ATTRAPPE) { setze("aus_fassung"); return; }
    let weg = false;
    let aufTipp: (() => void) | null = null;
    neuAnmelden = () => {
      void hole<{ oeffentlich: string | null }>("/meldungen/push")
        .then(({ oeffentlich }) => (oeffentlich && Notification.permission === "granted"
          ? abonnieren(oeffentlich, geraetename()) : undefined))
        .catch(scheitern);
    };
    const geraet = geraetename();
    void (async () => {
      const iphone = /iPhone|iPad/.test(navigator.userAgent);
      const installiert = window.matchMedia?.("(display-mode: standalone)").matches
        || (navigator as Navigator & { standalone?: boolean }).standalone === true;
      if (!("serviceWorker" in navigator) || !("PushManager" in window) || !("Notification" in window)) {
        setze(iphone && !installiert ? "iphone_installieren" : "kein_browser");
        return;
      }
      const { oeffentlich } = await hole<{ oeffentlich: string | null }>("/meldungen/push").catch(() => ({ oeffentlich: null }));
      if (weg) return;
      if (!oeffentlich) { setze("aus_fassung"); return; }
      if (Notification.permission === "denied") { setze("verweigert"); return; }
      if (Notification.permission === "granted") {
        await abonnieren(oeffentlich, geraet).catch(scheitern);
        return;
      }
      /* Noch nicht gefragt: beim ersten Tipp. */
      setze("fragt");
      aufTipp = () => {
        if (aufTipp) window.removeEventListener("pointerdown", aufTipp, true);
        aufTipp = null;
        void Notification.requestPermission().then((erlaubnis) => {
          if (erlaubnis === "granted") return abonnieren(oeffentlich, geraet);
          setze(erlaubnis === "denied" ? "verweigert" : "fragt");
          return undefined;
        }).catch(scheitern);
      };
      window.addEventListener("pointerdown", aufTipp, true);
    })();
    return () => {
      weg = true;
      if (aufTipp) window.removeEventListener("pointerdown", aufTipp, true);
    };
  }, []);
  return null;
}

/** Die Zeile in der Glocke: wie es steht, nicht zum Umschalten. */
export function PushZeile() {
  const t = useT();
  const s = usePushstand();
  if (s === "laedt") return null;
  return (
    <p className="glocke-hinweis">
      {t(`meldungen.push.${s}`)}
      {s === "fehler" ? <><br /><code>{pushFehler()}</code></> : null}
    </p>
  );
}
